import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useFormatMoneda } from '../utils/moneda';

const fmtFecha = f => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const estadoColor = {
  pendiente: { bg: '#172554', color: '#93c5fd' },
  vencida:   { bg: '#450a0a', color: '#fca5a5' },
  pagada:    { bg: '#052e16', color: '#86efac' },
  parcial:   { bg: '#1c1917', color: '#fcd34d' },
};
const creditoColor = {
  activo:       { bg: '#172554', color: '#93c5fd' },
  cancelado:    { bg: '#052e16', color: '#86efac' },
  vencido:      { bg: '#450a0a', color: '#fca5a5' },
  refinanciado: { bg: '#1c1917', color: '#fcd34d' },
};

export default function ClienteDetalle() {
  const fmt = useFormatMoneda();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expandido, setExpandido] = useState(null);

  function cargar() {
    api.get(`/clientes/${id}/estado-cuenta`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Error cargando estado de cuenta'));
  }

  useEffect(() => { cargar(); }, [id]);

  if (!data) return <div style={{ color: 'var(--text-3)', padding: '2rem', textAlign: 'center' }}>Cargando...</div>;

  const { cliente, resumen, creditos, proxima_cuota } = data;
  const pct = resumen.deuda_total > 0 ? Math.min(100, (resumen.total_pagado / (resumen.total_pagado + resumen.deuda_total)) * 100) : 100;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button onClick={() => navigate(-1)} style={s.btnBack}>← Volver</button>
        <div>
          <h2 style={{ margin: 0, color: 'var(--text-1)', fontSize: '1.4rem' }}>{cliente.nombre}</h2>
          <span style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>{cliente.tipo_documento} {cliente.documento} · {cliente.zona || 'Sin zona'} · {cliente.telefono || 'Sin teléfono'}</span>
        </div>
      </div>

      {/* Resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Deuda total', value: fmt(resumen.deuda_total), color: '#f87171' },
          { label: 'Deuda vencida', value: fmt(resumen.deuda_vencida), color: '#fbbf24' },
          { label: 'Total pagado', value: fmt(resumen.total_pagado), color: '#4ade80' },
          { label: 'Créditos activos', value: resumen.creditos_activos, color: '#38bdf8' },
        ].map(m => (
          <div key={m.label} style={s.metricCard}>
            <div style={{ color: 'var(--text-3)', fontSize: '0.75rem', marginBottom: 4 }}>{m.label}</div>
            <div style={{ color: m.color, fontSize: '1.4rem', fontWeight: 800 }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Barra de progreso total */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: 8 }}>
          <span>Progreso de pago</span>
          <span>{pct.toFixed(1)}% cancelado</span>
        </div>
        <div style={{ height: 10, background: 'var(--bg-app)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#4ade80' : '#38bdf8', transition: 'width 0.4s', borderRadius: 6 }} />
        </div>
      </div>

      {/* Próxima cuota */}
      {proxima_cuota && (
        <div style={{ background: proxima_cuota.dias_restantes < 0 ? '#450a0a' : '#172554', border: `1px solid ${proxima_cuota.dias_restantes < 0 ? '#7f1d1d' : '#1e3a8a'}`, borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ color: 'var(--text-2)', fontSize: '0.75rem', marginBottom: 4 }}>Próxima cuota a pagar</div>
            <div style={{ color: 'var(--text-1)', fontWeight: 700 }}>
              Cuota #{proxima_cuota.numero} — {fmt(proxima_cuota.monto - proxima_cuota.monto_pagado)}
            </div>
            <div style={{ color: 'var(--text-3)', fontSize: '0.8rem' }}>Vence: {fmtFecha(proxima_cuota.fecha_vence)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 900, color: proxima_cuota.dias_restantes < 0 ? '#f87171' : proxima_cuota.dias_restantes <= 3 ? '#fbbf24' : '#4ade80' }}>
              {proxima_cuota.dias_restantes < 0 ? `${Math.abs(proxima_cuota.dias_restantes)}d vencida` : proxima_cuota.dias_restantes === 0 ? 'Hoy' : `En ${proxima_cuota.dias_restantes}d`}
            </span>
          </div>
        </div>
      )}

      {/* Lista de créditos */}
      <h3 style={{ color: 'var(--text-2)', fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.75rem' }}>Historial de créditos</h3>
      {creditos.length === 0 && <p style={{ color: 'var(--text-4)', textAlign: 'center', padding: '2rem' }}>Este cliente no tiene créditos registrados.</p>}

      {creditos.map(cr => {
        const abierto = expandido === cr.id;
        const col = creditoColor[cr.estado] || creditoColor.activo;
        const cuotasPagadas = cr.cuotas.filter(c => c.estado === 'pagada').length;
        const totalCuotas = cr.cuotas.length;
        const pctCr = totalCuotas > 0 ? (cuotasPagadas / totalCuotas) * 100 : 0;

        return (
          <div key={cr.id} style={{ background: 'var(--bg-card)', borderRadius: 12, marginBottom: '0.75rem', border: '1px solid var(--border)', overflow: 'hidden' }}>
            {/* Cabecera del crédito */}
            <div
              onClick={() => setExpandido(abierto ? null : cr.id)}
              style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ background: col.bg, color: col.color, fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>{cr.estado.toUpperCase()}</span>
                  <span style={{ color: 'var(--text-1)', fontWeight: 700 }}>Crédito #{cr.id}</span>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.8rem' }}>{cr.frecuencia}</span>
                </div>
                <div style={{ color: 'var(--text-2)', fontSize: '0.8rem' }}>
                  Capital: {fmt(cr.monto)} · Inicio: {fmtFecha(cr.fecha_inicio)} · {cuotasPagadas}/{totalCuotas} cuotas pagadas
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: cr.saldo_pendiente > 0 ? '#f87171' : '#4ade80', fontWeight: 700, fontSize: '1.1rem' }}>{fmt(cr.saldo_pendiente)}</div>
                <div style={{ color: 'var(--text-4)', fontSize: '0.75rem' }}>saldo</div>
              </div>
            </div>

            {/* Barra mini */}
            <div style={{ height: 4, background: 'var(--bg-app)' }}>
              <div style={{ width: `${pctCr}%`, height: '100%', background: pctCr === 100 ? '#4ade80' : '#38bdf8' }} />
            </div>

            {/* Cuotas expandibles */}
            {abierto && (
              <div style={{ borderTop: '1px solid var(--border)' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-app)', color: 'var(--text-3)', textAlign: 'left' }}>
                        {['#', 'Vence', 'Monto', 'Pagado', 'Saldo', 'Estado'].map(h => (
                          <th key={h} style={{ padding: '0.5rem 1rem' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cr.cuotas.map(cuota => {
                        const ec = estadoColor[cuota.estado] || estadoColor.pendiente;
                        return (
                          <tr key={cuota.id} style={{ borderBottom: '1px solid #1e293b', color: 'var(--text-1s)' }}>
                            <td style={{ padding: '0.5rem 1rem' }}>{cuota.numero}</td>
                            <td style={{ padding: '0.5rem 1rem' }}>{fmtFecha(cuota.fecha_vence)}</td>
                            <td style={{ padding: '0.5rem 1rem' }}>{fmt(cuota.monto)}</td>
                            <td style={{ padding: '0.5rem 1rem', color: '#4ade80' }}>{fmt(cuota.monto_pagado)}</td>
                            <td style={{ padding: '0.5rem 1rem', color: cuota.monto - cuota.monto_pagado > 0 ? '#f87171' : '#64748b' }}>{fmt(cuota.monto - cuota.monto_pagado)}</td>
                            <td style={{ padding: '0.5rem 1rem' }}>
                              <span style={{ background: ec.bg, color: ec.color, fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>{cuota.estado}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const s = {
  btnBack: { background: 'var(--bg-hover)', color: 'var(--text-2)', border: 'none', borderRadius: 8, padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' },
  metricCard: { background: 'var(--bg-card)', borderRadius: 12, padding: '1rem', border: '1px solid var(--border)' },
};
